/* PIPE-INST T5.2 (firma #136) — EL SISTEMA DE DISEÑO DEL CARRIL INSTITUCIONAL.
 *
 * ── POR QUÉ EXISTE ESTE ARCHIVO ─────────────────────────────────────────────
 *
 * T5.1 (#132) hizo el «de-carding»: retiró los marcos gigantes que envolvían
 * secciones enteras, y en eso acertó — eran marcos dentro de marcos y el borde
 * de fuera competía con el de las cards que de verdad importan. Lo que no hizo
 * fue dar jerarquía a cambio, así que el resultado se ve inacabado: todo flota
 * sobre el mismo negro, todo pesa lo mismo, y las cuatro pantallas del profesor
 * quedaron como un wireframe.
 *
 * ── LA CAUSA RAÍZ, Y ES MEDIBLE ─────────────────────────────────────────────
 *
 * El fondo de la app es `#0e0e0e` (`--background: 0 0% 5.5%`, index.css:9, y el
 * `body` lo repite en :45). Las cards del carril se pintan `bg-[#0d0d0d]`, que
 * es **UN PUNTO MÁS OSCURO QUE EL FONDO**. No se elevan: se hunden. Todo el
 * trabajo de separación lo hace un borde `#262626` de 1 px, y por eso la
 * pantalla se lee como una lista de rectángulos dibujados sobre nada.
 *
 * Y hay una ironía en el origen: la clase que se retiró, `glass-card`, declara
 * `background: #181818` (index.css:89) — el token `--card` de la casa, DIEZ
 * puntos por encima del fondo. T5.1 la quitó por su `transition: all .3s` (la
 * enfermedad medida en flashcards, COS-18 y COS-32) y se llevó por delante su
 * superficie. **Este módulo recupera el color y deja fuera la transición.**
 *
 * Censo del desastre que esto cierra: las cuatro pantallas usaban SEIS grises
 * casi-negros distintos —`#0b0b0b`, `#0d0d0d`, `#101010`, `#111`, `#131313`,
 * `#161616`— elegidos archivo por archivo. Tres de ellos por debajo del fondo.
 *
 * ── LA REGLA DE ORO ─────────────────────────────────────────────────────────
 *
 * ROPA, NO CUERPO. Aquí sólo hay cadenas de clases. Ni un dato, ni un handler,
 * ni un testid. Y ni una transición sobre `all`: cuando algo tenga que animar,
 * se declara campo por campo en su sitio.
 */

/* ════════════════════════════════════════════════════════════════════════════
 *  1 — LAS CAPAS
 *
 *  Tres alturas y no más. Cada una es un paso REAL de luminancia sobre la de
 *  abajo, para que la jerarquía se vea sin tener que leer los bordes:
 *
 *      fondo de la app   #0e0e0e   (14,14,14)
 *      CAPA              #181818   (24,24,24)   +10
 *      CAPA_HUNDIDA      #101010   (16,16,16)    −8 respecto a CAPA
 *
 *  `CAPA_HUNDIDA` es para lo que va DENTRO de una card y tiene que leerse como
 *  un hueco: las cajas de texto de la edición, los sub-bloques de cada
 *  distractora. Sobre el fondo pelado no se usa nunca — ahí no hundiría nada,
 *  se confundiría con él.
 * ════════════════════════════════════════════════════════════════════════════ */

/** La superficie de una card real: documento, tema, pregunta. */
export const CAPA = 'rounded-xl border border-[#262626] bg-[#181818]';

/** Un hueco DENTRO de una capa. Nunca sobre el fondo pelado. */
export const CAPA_HUNDIDA = 'rounded-lg border border-[#242424] bg-[#101010]';

/** La elevación al pasar el puntero, para las cards que se pueden tocar.
 *
 *  ── T5.3 (firma #141a): EL HOVER NO MUEVE NADA, Y ESTÁ MEDIDO ──────────────
 *
 *  El smoke de cierre de T5.2 lo cazó: «al pasar el mouse por encima de las
 *  cards de tema se mueve toda la pantalla — no se mueve casi nada pero es
 *  notorio y no se siente bien».
 *
 *  LO PRIMERO ES LO QUE **NO** ERA, porque se midió en Chrome de verdad y no se
 *  dedujo: la geometría **no se movía**. Con el ratón real encima de una card,
 *  el censo de `getBoundingClientRect()` de TODOS los nodos de la pantalla da
 *  **cero elementos cambiados** —posición, tamaño y `scrollHeight` idénticos al
 *  píxel—, y el barrido del CSS compilado confirma por qué: de las 24 clases de
 *  hover que usan las cuatro pantallas del carril, NI UNA toca una propiedad de
 *  layout. No hay borde que engorde, ni transform, ni scrollbar que aparezca.
 *
 *  LO QUE SÍ ERA, Y SON DOS COSAS QUE SUMAN:
 *
 *  1. **La sombra pintaba FUERA de la caja.** El diff de píxeles del hover
 *     contra el reposo da 1,097 píxeles cambiados por fuera del border-box de la
 *     card —una orla de 1 a 3 px por los lados y por arriba—, y ése es
 *     literalmente el contorno de la tarjeta creciendo más allá de sí misma.
 *     Y no compensaba nada: con `blur 10` contra `spread -6`, el delta máximo de
 *     esa orla es **1 sobre 255**. Una sombra invisible que sólo aporta el
 *     defecto. Se retira entera.
 *
 *  2. **El borde casi DOBLABA de peso.** `#262626` (luminancia 38) → `#3a3a3a`
 *     (58) sobre un fondo de 14: el contraste del borde contra el fondo pasaba
 *     de 24 a 44. Un borde de 1 px que de golpe pesa el doble se lee como un
 *     borde de 2 px — que es la hipótesis del encargo, ocurriendo en PESO y no
 *     en píxeles. Mientras tanto el relleno subía sólo +5, así que lo único que
 *     se movía en el ojo era el contorno.
 *
 *  EL ARREGLO ES EL PATRÓN DE LA CASA, no uno inventado. `glass-card:hover`
 *  —las cards de «Mis grupos», `index.css:98-101`— cambia el borde a
 *  `rgba(255,79,0,0.2)`: compuesto sobre la capa da (70,35,19), o sea luminancia
 *  **41** contra los 38 del borde en reposo. **Cambia de tono sin cambiar de
 *  peso**, que es exactamente lo que distingue un hover que «resalta» de uno que
 *  «crece». Y de paso cierra la firma #141b: las cards del banco piden ese mismo
 *  naranja, y ahora lo tienen por el sistema en vez de por una copia.
 *
 *  El relleno sube a `#202020` (+8 sobre la capa, contra los +5 de antes): la
 *  señal que la sombra fingía dar, puesta donde no puede leerse como geometría.
 *
 *  La transición sigue declarada campo por campo y NUNCA sobre `all`: estas
 *  cards cambian de alto cada 4 s por el sondeo. `hoverable:` es la variante del
 *  repo — en teléfono un `:hover` se queda pegado.
 *
 *  LA REGLA, PARA LA TANDA SIGUIENTE: aquí sólo entran propiedades de PINTURA
 *  (color, background-color, border-color). Nada que pinte fuera del border-box
 *  —`shadow`, `ring`, `outline`— y nada de layout. Lo fija
 *  `vestidoDelCarril.hover.test.js`, que barre las clases de hover de las cuatro
 *  pantallas y no sólo las de este módulo. */
export const CAPA_TOCABLE =
  'transition-[background-color,border-color] duration-150 '
  + 'hoverable:hover:border-[#FF4F00]/20 hoverable:hover:bg-[#202020]';

/* PROFE-DESCARGAS-REMATES-ALUMNO (PR #349, P-1 / #325) — LA BANDA DE UN PLEGABLE.
 *
 * Nació como el mismo gris al que `CAPA_TOCABLE` lleva la tarjeta al pasar el
 * puntero (#202020), en reposo y sólo sobre la banda del título: un tono más claro
 * que el cuerpo, para que se lea como la parte que se toca. Lo usan los dos
 * plegables de la página del grupo (código y alumnos).
 *
 * ── REMATES-350 (PR #351, R-3 / #347) — TRES GRISES CON ESCALONES PAREJOS ────
 *
 * Medido con la fórmula WCAG de luminancia relativa, el plegable tenía tres grises
 * que a simple vista eran uno: fondo #0e0e0e → cuerpo #181818 daba 1.087 : 1 y
 * cuerpo → banda #202020, 1.090 : 1. La firma pide cada escalón visible (≥ 1.35 : 1)
 * y los dos lo más parejos posible. Con la banda en #202020 ningún cuerpo cabe
 * entre los dos (haría falta que el cuerpo estuviera POR DEBAJO del fondo), así que
 * el cuerpo sube a #2b2b2b (1.363 : 1 sobre el fondo) y la banda sube lo mínimo a
 * #404040 (1.366 : 1 sobre el cuerpo). El fondo de la página no se toca.
 *
 * `CAPA` (#181818) NO se mueve: es la capa de las cards del banco, de los temas y de
 * las preguntas, y esos no eran parte de la firma. El plegable estrena su propio
 * token (`CAPA_PLEGABLE`) con el mismo borde y las mismas esquinas. Sin cambio de
 * color al pasar el puntero ni al tocar (#342 sigue); el link para unirse sigue en
 * naranja. Lo fija `pages/GroupDetailPage.profeMaterial350.test.js` (R-3). */
export const BANDA_TOCABLE = 'bg-[#404040]';

/** La superficie de un PLEGABLE de la página del grupo (código, alumnos): la capa
 *  del carril con el cuerpo un escalón más claro que el fondo (ver arriba). */
export const CAPA_PLEGABLE = 'rounded-xl border border-[#262626] bg-[#2b2b2b]';

/* remates-1-3-0c (Q6, W10): LA SECCION PLEGABLE DE LA PAGINA DE TEMAS ("Temas del documento" y
 * "Cards publicadas"). Antes era la misma capa que las tarjetas de adentro (mismo fondo, borde y
 * radio) y no se distinguia que contenia a que. El diseno de la marca, fijo:
 *   - la seccion es una capa MAS OSCURA que las tarjetas: fondo 0d0d0d, borde de 1 px 262626,
 *     radio de 16, sin sombra (y recorta lo de adentro, para que el fondo de la cabecera al pasar
 *     el raton respete el radio);
 *   - la cabecera es un renglon de 56 px: la guia de la marca (una barra naranja de 3 x 20 px) a la
 *     izquierda del titulo, el titulo a 18 px seminegrita blanco, un chip gris de conteo a la
 *     derecha (fondo 1a1a1a, texto a0a0a0, 12 px, pildora) y la flecha; al pasar el raton (solo con
 *     puntero fino) el fondo sube a 111111, que es pintura y nada mas (la regla de la seccion 4);
 *   - el cuerpo lleva 16 px de relleno y, arriba, la linea de 1 px 1f1f1f de lado a lado; plegado,
 *     el cuerpo no se ve y la linea tampoco. Las tarjetas de adentro conservan su capa. */
/* t7-2a (B5, leccion 120): el CONTENEDOR pasa a negro calido (fondo 1a1210, borde 33261d) y la
 * linea de arriba del cuerpo a 2a1f18. Decia: fondo 0d0d0d, borde 262626 y linea 1f1f1f. Lo
 * demas de la capa (cabecera, guia, titulo, chip, relleno) no se movio. */
export const CAPA_DE_SECCION = 'rounded-2xl border border-[#33261d] bg-[#1a1210] overflow-hidden';
export const CABECERA_DE_SECCION = 'w-full h-14 flex items-center gap-3 px-4 text-left hoverable:bg-[#111111] transition-colors';
export const GUIA_DE_SECCION = 'shrink-0 w-[3px] h-5 rounded-full bg-[#FF6B00]';
export const TITULO_DE_SECCION_PLEGABLE = 'text-lg font-semibold text-white';
export const CHIP_DE_CONTEO = 'shrink-0 rounded-full bg-[#1a1a1a] text-[#a0a0a0] text-xs font-normal px-2.5 py-0.5';
export const CUERPO_DE_SECCION = 'p-4 space-y-3 border-t border-[#2a1f18]';

/* ════════════════════════════════════════════════════════════════════════════
 *  2 — LA ESCALA TIPOGRÁFICA
 *
 *  Cinco niveles, y cada uno tiene que distinguirse del vecino por PESO o por
 *  TAMAÑO, no sólo por color. Salen de la moda del producto (censo de
 *  `SettingsPage`, `GroupsPage`, `AdaptiveStudy`, `Analytics`), con dos
 *  correcciones deliberadas que se explican abajo.
 * ════════════════════════════════════════════════════════════════════════════ */

/** Nivel 1 — el título de la pantalla.
 *
 *  SUBE DE `text-lg` A `text-2xl`, que es la moda real de la casa
 *  (`GroupsPage:148`, `GroupDetailPage:251`, `SettingsPage:216`). Las dos
 *  pantallas de dentro del banco se habían quedado en `text-lg font-semibold`,
 *  o sea al MISMO tamaño que sus títulos de sección: entrar a un documento se
 *  sentía como no haber llegado a ninguna parte. */
export const TITULO_PANTALLA = 'text-2xl font-bold text-white';

/** Nivel 2 — el título de una sección dentro de la pantalla. */
export const TITULO_SECCION = 'text-base font-semibold text-white';

/** Nivel 3 — el cuerpo. */
export const CUERPO = 'text-sm text-[#c8c8c8]';

/** Nivel 3b — el cuerpo secundario: instrucciones, subtítulos, explicaciones. */
export const CUERPO_TENUE = 'text-sm text-[#9a9a9a]';

/** Nivel 4 — la etiqueta que nombra un campo.
 *
 *  AQUÍ MUERE EL GRIS FANTASMA. Era `text-[11px] text-[#6a6a6a]`: 11 px en
 *  #6a6a6a sobre una superficie casi negra da un contraste de ~3.4:1, por
 *  debajo del mínimo legible incluso para texto grande — y estas etiquetas son
 *  lo que le dice al profesor qué está escribiendo en cada caja. Sube a
 *  `#9a9a9a` (~6.4:1 sobre `#181818`) y gana `font-medium`. El tamaño se queda
 *  en 11 px porque la etiqueta tiene que seguir siendo más chica que su
 *  contenido; lo que estaba mal no era el tamaño, era el color. */
export const ETIQUETA =
  'text-[11px] font-medium uppercase tracking-wider text-[#9a9a9a]';

/** Nivel 5 — el dato meta de un renglón (páginas, conteos, notas al pie). */
export const META = 'text-[11px] text-[#8a8a8a]';

/* ════════════════════════════════════════════════════════════════════════════
 *  3 — EL RITMO
 *
 *  Una escala fija y corta, de la de Tailwind, aplicada con consistencia. Los
 *  huecos muertos de T5.1 salieron de elegir el espaciado renglón por renglón.
 *
 *      RITMO_PANTALLA   space-y-6   entre las secciones grandes
 *      RITMO_SECCION    space-y-3   dentro de una sección
 *      RITMO_LISTA      space-y-2   entre las cards de una lista
 *      RELLENO_CAPA     px-3 py-2.5 dentro de una card compacta
 * ════════════════════════════════════════════════════════════════════════════ */

export const RITMO_PANTALLA = 'space-y-6';
export const RITMO_SECCION = 'space-y-3';
export const RITMO_LISTA = 'space-y-2';
export const RELLENO_CAPA = 'px-3 py-2.5';

/* ════════════════════════════════════════════════════════════════════════════
 *  4 — LO QUE NO SE TOCA
 *
 *  LA REGLA NUEVA DE T5.3, PRIMERO, porque es la que se rompe sin querer: **un
 *  hover de este carril sólo puede cambiar PINTURA** —color, background-color,
 *  border-color— y nunca nada que pinte fuera del border-box (`shadow`, `ring`,
 *  `outline`) ni que toque el bulto (anchos, altos, márgenes, transform, tamaño
 *  de letra). No es una preferencia: es el defecto #141a, medido. Lo barre
 *  `lib/vestidoDelCarril.hover.test.js` sobre las CUATRO pantallas, no sólo
 *  sobre este módulo.
 *
 *  Y lo demás, escrito para que nadie lo «unifique» en la tanda siguiente:
 *
 *    · los botones naranjas macizos (`bg-[#FF6B00]`) y su `min-h-[44px]`;
 *    · los chips de estado y su precedencia (trabajo vivo por encima de
 *      «Revisado» — la lección de T5);
 *    · las pestañas con subrayado naranja + peso de letra (T5.1);
 *    · los banners de la casa (`bannerDeLaCasa`), que son `fixed` y viven fuera
 *      de estas capas;
 *    · la concha de los modales, idéntica en seis sitios del producto;
 *    · la zona destructiva de `GroupDetailPage`, que sigue siendo tarjeta a
 *      propósito: su marco es parte de la fricción (firma #132).
 * ════════════════════════════════════════════════════════════════════════════ */
